import { Component } from '@angular/core';
import { SharesApiProviderService } from '@core/api';
import { Observable, catchError, finalize, of, tap } from 'rxjs';
import { EStatusCode } from 'src/app/core/helpers/error-codes';
import { LimitExceededService } from 'src/app/project/components/change-limit-modal/limit-exceeded.service';
import { ModalService } from 'src/app/project/components/modal/modal.service';
import { PromptService } from 'src/app/project/components/prompt/prompt.service';
import { TranslationPipe } from 'src/app/project/features/translate/translation.pipe';
import { getErrorMessage } from 'src/app/project/helpers/database/get-error-message';
import { ResponseErrorService } from '../../errors/response-error.service';
import { AccountSharesService } from '../../share/account-shares.service';
import { TCreateAccountAdminDTO } from '../../share/share.model';
import { TWorkspacesById } from '../../workspace/workspace.model';
import { WorkspaceService } from '../../workspace/workspace.service';
import { AccountService } from '../account-service/account.service';
import { TAccount } from '../account.model';
import { TAddAccountAdminModalData } from './new-account-admin-modal.consts';

@Component({
  selector: 'pp-new-account-admin-modal',
  templateUrl: './new-account-admin-modal.component.html',
  styleUrls: ['./new-account-admin-modal.component.scss'],
  standalone: false,
})
export class NewAccountAdminModalComponent {
  processing: boolean;
  account: TAccount;
  workspaces: TWorkspacesById;
  upgradingUser = false;
  showingConfirmation: boolean;
  warningMessages: string[] = [];
  emailList: string[] = [];

  private accountId: string;

  constructor(
    private modalService: ModalService,
    private accountService: AccountService,
    private workspaceService: WorkspaceService,
    private translationPipe: TranslationPipe,
    private sharesApiProviderService: SharesApiProviderService,
    private promptService: PromptService,
    private responseErrorService: ResponseErrorService,
    private accountSharesService: AccountSharesService,
    private limitExceededService: LimitExceededService,
  ) {
    this.setModalData();
    this.setWarningMessages();

    this.account = this.accountService.getAccount(this.accountId);
    this.workspaces = this.workspaceService.getWorkspaces();
  }

  hideModal(): void {
    this.modalService.hideModal();
  }

  setEmailList(emailList: string[]): void {
    this.emailList = emailList;
  }

  goToConfirmStep(): void {
    this.showingConfirmation = true;
  }

  addAccountAdmins(): void {
    if (this.processing) {
      return;
    }

    this.sendAddAccountAdminRequest(true);
  }

  private sendAddAccountAdminRequest(hideModal: boolean): Promise<boolean> {
    return new Promise((resolve) => {
      this.processing = true;

      const requestBody: TCreateAccountAdminDTO[] = this.getRequestBody();

      this.sharesApiProviderService
        .createAccountAdmin(requestBody)
        .pipe(
          tap(() => {
            this.handleCreateAccountAdminResponse(hideModal);
            resolve(true);
          }),
          catchError((error) => this.handleCreateAccountAdminError(error)),
          finalize(() => {
            this.processing = false;
          }),
        )
        .subscribe();
    });
  }

  private getRequestBody(): TCreateAccountAdminDTO[] {
    return this.emailList.map((email) => ({
      email,
    }));
  }

  private handleCreateAccountAdminError(error: any): Observable<null> {
    if (error.status === EStatusCode.UPGRADE_REQUIRED) {
      this.limitExceededService.showLimitsExceededModal();

      return of(null);
    } else {
      getErrorMessage(error).then((message) => {
        this.promptService.showError(message);
      });

      return this.responseErrorService.handleRequestError(error, 'Failed to add account admin');
    }
  }

  private handleCreateAccountAdminResponse(hideModal: boolean = true): void {
    const prompt = this.translationPipe.transform('new_account_admin_success');
    this.promptService.showSuccess(prompt);
    this.accountSharesService.resetAccountShares(this.accountId).subscribe();

    if (hideModal) {
      this.hideModal();
    }
  }

  private checkUpgradingUser(modalData: TAddAccountAdminModalData): void {
    this.upgradingUser = !!modalData.user;

    if (this.upgradingUser) {
      this.emailList = [modalData.user.email];
    }
  }

  private setWarningMessages(): void {
    this.warningMessages = [this.translationPipe.transform('new_account_admin_warning')];
  }

  private setModalData(): void {
    const modalData: TAddAccountAdminModalData = this.modalService.getModal().data;

    this.accountId = modalData.accountId;

    this.checkUpgradingUser(modalData);
  }
}
